import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import {getSharedSmartChipProps, SharedSmartChipProps} from 'ui-library/chips/ChipUtil';

import {BulkChipItemPrefetchQuery} from './__generated/BulkChipItemPrefetchQuery';
import ChipPopoverContent from './ChipPopoverContent';
import {NO_ACCESS_CHIP_TEXT} from './NoAccessChip';
import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

type ProcedureMethodExecutionInstance = ExtractByTypename<
  BulkChipItemPrefetchQuery['procedureMethodExecutionInstances'][number],
  'ProcedureMethodExecutionInstanceNode'
>;

interface ProcedureMethodExecutionInstanceChipFields {
  procedureRunName: string;
  planMethodDefinitionVersionName: string;
  procedureRunConditionName: string;
  replicateNumber: number;
}

function getProcedureMethodExecutionInstanceChipFields(
  procedureMethodExecutionInstance: ProcedureMethodExecutionInstance
): ProcedureMethodExecutionInstanceChipFields {
  const procedureRunName = procedureMethodExecutionInstance.procedureRun.name;
  const planMethodDefinitionVersionName = procedureMethodExecutionInstance.planMethodDefinitionVersion.name;
  const {replicateNumber, procedureRunCondition} =
    procedureMethodExecutionInstance.procedureRunConditionReplicate;
  const procedureRunConditionName = procedureRunCondition.name;
  return {
    procedureRunName,
    planMethodDefinitionVersionName,
    replicateNumber,
    procedureRunConditionName,
  };
}

export function ProcedureMethodExecutionInstanceChipPopoverContent({
  pmeiChipFields,
  isInaccessible,
  procedureType,
}: {
  pmeiChipFields: ProcedureMethodExecutionInstanceChipFields;
  isInaccessible: boolean | undefined;
  procedureType: ProcedureType;
}): JSX.Element | null {
  if (isInaccessible) {
    return <>{NO_ACCESS_CHIP_TEXT}</>;
  }
  const {procedureRunName, planMethodDefinitionVersionName, procedureRunConditionName, replicateNumber} =
    pmeiChipFields;
  const fields = [
    {
      label: `${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType)} run`,
      value: procedureRunName,
    },
    {label: 'Unit operation', value: planMethodDefinitionVersionName},
    {label: 'Condition', value: procedureRunConditionName},
    {label: 'Replicate', value: `${replicateNumber}`},
  ];
  return <ChipPopoverContent info={{fields}} />;
}

export default function ProcedureMethodExecutionInstanceChip(
  props: SharedSmartChipProps<SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE>
): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: procedureMethodExecutionInstance,
    handleClickLink,
    isInaccessible,
  } = useSmartChipQuery<SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE>({
    id,
    onClick,
  });

  let procedureMethodExecutionInstanceItem;
  let popoverContent;
  if (procedureMethodExecutionInstance) {
    const pmeiChipFields = getProcedureMethodExecutionInstanceChipFields(procedureMethodExecutionInstance);
    procedureMethodExecutionInstanceItem = {
      ...procedureMethodExecutionInstance,
      name: `${pmeiChipFields.procedureRunName} ${pmeiChipFields.planMethodDefinitionVersionName} ${pmeiChipFields.procedureRunConditionName} - ${pmeiChipFields.replicateNumber}`,
    };
    popoverContent = (
      <ProcedureMethodExecutionInstanceChipPopoverContent
        pmeiChipFields={pmeiChipFields}
        isInaccessible={isInaccessible}
        procedureType={procedureMethodExecutionInstance.procedureRun.procedureType}
      />
    );
  }

  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={null}
      isInaccessible={isInaccessible}
      item={procedureMethodExecutionInstanceItem}
      onClick={handleClickLink}
      popoverContentOverride={popoverContent}
      url={null}
      isReadonly={true}
    />
  );
}
